import { useState, useEffect } from "react";
import { adminService } from "./apiServices";

export interface PlatformSettings {
  name: string;
  supportEmail: string;
  supportMobile: string;
  companyName: string;
  address: string;
  gst: string;
  timezone: string;
  dateFormat: string;
  currency: string;
}

export const DEFAULT_PLATFORM_SETTINGS: PlatformSettings = {
  name: "MandatePro",
  supportEmail: "support@mandatepro.in",
  supportMobile: "+91 98765 43210",
  companyName: "MandatePro Fintech Pvt. Ltd.",
  address: "4th Floor, Tech Tower, Sector 62, Noida, Uttar Pradesh 201309",
  gst: "09AABCMandate1Z5",
  timezone: "Asia/Kolkata",
  dateFormat: "DD MMM YYYY",
  currency: "INR",
};

const STORAGE_KEY = "mandatepro_general_settings";
const EVENT_NAME = "mandatepro_settings_changed";

export function getCachedSettings(): PlatformSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return { ...DEFAULT_PLATFORM_SETTINGS, ...JSON.parse(raw) };
    }
  } catch (e) {
    // Ignore JSON error
  }
  return DEFAULT_PLATFORM_SETTINGS;
}

export function saveCachedSettings(settings: Partial<PlatformSettings>) {
  try {
    const current = getCachedSettings();
    const updated = { ...current, ...settings };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: updated }));
    return updated;
  } catch (e) {
    return DEFAULT_PLATFORM_SETTINGS;
  }
}

export async function loadRemoteSettings(): Promise<PlatformSettings> {
  try {
    const res = await adminService.getSettings();
    if (res && res.data && res.data.general) {
      return saveCachedSettings(res.data.general);
    }
  } catch (err) {
    // Silently fall back to cached
  }
  return getCachedSettings();
}

export function usePlatformSettings() {
  const [settings, setSettings] = useState<PlatformSettings>(getCachedSettings);

  useEffect(() => {
    // 1. Fetch remote settings from DB
    loadRemoteSettings().then((remote) => {
      setSettings(remote);
    });

    // 2. Listen for local broadcasts
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent<PlatformSettings>;
      if (customEvent.detail) {
        setSettings(customEvent.detail);
      }
    };

    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  return settings;
}
